import { useEffect, useState } from 'react';
import { clearFromLocalStorage, loadFromLocalStorage, saveToLocalStorage } from '@utils/storage';
// import { isNewBrowserSession } from '@utils/session';
import { useTranslation } from 'react-i18next';

type EndpointState<T> = {
    data: T | null;
    loading: boolean;
    ttl?: number;
    lastResponseTime: Date | null;
};

type EndpointsState = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: EndpointState<any>;
};

const initialEndpointState = { data: null, loading: false };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useEndpointState = (endpointKeys: any) => {
    const { i18n } = useTranslation();
    const isLoggedIn = loadFromLocalStorage('login') !== null;
    const [initiallyLoggedIn] = useState<boolean>(isLoggedIn);
    // // Check for new browser session only once on mount (outside useEffect to avoid race conditions)
    // const [wasNewBrowserSession] = useState<boolean>(() => isNewBrowserSession());

    const endpoints = Object.keys(endpointKeys);

    const [endpointsState, setEndpointsState] = useState<EndpointsState>(() => {
        const initialState: EndpointsState = {};

        endpoints.forEach((key) => {
            const ttl = endpointKeys[key].ttl;
            const data = endpointKeys[key].storeOnLocalStorage ? loadFromLocalStorage(key) : initialEndpointState.data;
            initialState[key] = {
                data,
                loading: false,
                ttl,
                lastResponseTime: null,
            };
        });

        return initialState;
    });

    const [changedLoginStatus, setChangedLoginStatus] = useState(false);

    // if clearOnNewSession is true
    // clear data if user logged in or logged out (not on new browser session - that's handled at init)
    useEffect(() => {
        if (isLoggedIn !== initiallyLoggedIn && !changedLoginStatus) {
            setChangedLoginStatus(true);

            // Only clear endpoints when login status actually changes (login/logout)
            endpoints.forEach((key) => {
                const clearOnNewSession = endpointKeys[key].clearOnNewSession;
                if (clearOnNewSession && key !== 'login') {
                    clearEndpointData(key);
                }
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn]);

    // if clearOnLanguageChange is true
    // clear data if language changes
    useEffect(() => {
        endpoints.forEach((key) => {
            const clearOnLanguageChange = endpointKeys[key].clearOnLanguageChange;
            if (clearOnLanguageChange) {
                clearEndpointData(key);
            }
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n.language]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const setEndpointState = (endpointKey: string, newState: Partial<EndpointState<any>>) => {
        setEndpointsState((prev) => {
            const updatedState = {
                ...prev,
                [endpointKey]: { ...prev[endpointKey], ...newState, lastResponseTime: new Date() },
            };
            if (newState.data !== undefined && endpointKeys[endpointKey].storeOnLocalStorage) {
                saveToLocalStorage(endpointKey, newState.data, endpointKeys[endpointKey].ttl);
            }
            return updatedState;
        });
    };

    const getEndpointState = (endpointKey: string) => {
        const now = new Date();
        const ttl = endpointsState[endpointKey]?.ttl;
        const lastResponseTime = endpointsState[endpointKey]?.lastResponseTime?.getTime();
        const expireTime = ttl && lastResponseTime && lastResponseTime + ttl;

        if (ttl && expireTime && now.getTime() > expireTime) {
            // if item expired, delete the item from storage and return null
            clearFromLocalStorage(endpointKey);
            return initialEndpointState;
        }

        return endpointsState[endpointKey] || initialEndpointState;
    };

    const clearEndpointData = (endpointKey: string) => {
        setEndpointState(endpointKey, { data: null });
    };

    return {
        setEndpointState,
        getEndpointState,
    };
};
